// Pure formatting helpers for the debrief tables. Kept out of the components so they can be unit
// tested without a DOM (tests/debrief/ui.test.ts).

import { card } from '../../sim/deck';
import type { Grid } from '../../sim/grid';
import { UNIT_DEFS } from '../../sim/scenario';
import type { CardId, Contact, Event, Graphic, Order, Target, Unit, UnitType } from '../../sim/types';
import type { TruthFrame } from '../../sim/sim';
import { clockHMS, gridRef } from '../reports';

/**
 * Sim ticks on the one clock, with seconds, for the order-log columns.
 *
 * These four columns used to print raw decimal minutes (`231.5`) beside an `H+03:52` in the same table,
 * which is the third of the three time formats the review found. The order timings differ by seconds -
 * that is the whole point of the issue/transmit/execute gap - so this is the one place seconds are shown.
 */
export function orderClock(tick: number): string {
  return clockHMS(tick);
}

/** One order's target in a table cell's worth of text. */
export function targetSummary(target: Target, graphics: Graphic[], units: Unit[], grid: Grid | null): string {
  switch (target.kind) {
    case 'point':
      return target.cell === undefined ? '—' : `grid ${gridRef(target.cell, grid)}`;
    case 'graphic': {
      const g = graphics.find((x) => x.id === target.graphicId);
      return g ? g.name : (target.graphicId ?? '—');
    }
    case 'unit': {
      const u = units.find((x) => x.id === target.unitId);
      return u ? u.callsign : `unit ${target.unitId ?? '?'}`;
    }
    case 'contact':
      return `contact ${target.unitId ?? '?'}`;
    case 'text':
      return target.text ?? '—';
    default:
      return '—';
  }
}

/** The order card's human label, tolerant of an id the deck no longer knows. */
export function cardLabel(id: CardId | string): string {
  try {
    return card(id as CardId).label;
  } catch {
    return String(id);
  }
}

/**
 * One line of plain English for an event in the decision log. Blue's own events read as the staff
 * reported them at the time; red-side events are ground truth the debrief is allowed to reveal, so they
 * are phrased as observations about the enemy rather than as radio traffic.
 */
export function eventLine(ev: Event, units: Unit[], grid: Grid | null): string {
  const unit = ev.unitId !== undefined ? units.find((u) => u.id === ev.unitId) : undefined;
  const cs = unit?.callsign ?? (ev.unitId !== undefined ? `UNIT ${ev.unitId}` : 'S-3');
  const where = ev.cell !== undefined ? ` vic ${gridRef(ev.cell, grid)}` : '';
  const enemy = ev.side === 1;
  const str = (v: unknown, f = ''): string => (typeof v === 'string' ? v : f);
  const num = (v: unknown, f = 0): number => (typeof v === 'number' ? v : f);

  switch (ev.kind) {
    case 'contact.new': {
      const conf = str(ev.payload.typeConf, 'unknown');
      const label = conf === 'unknown' ? 'unidentified contact'
        : conf === 'vehicle' ? 'vehicle'
        : (UNIT_DEFS[conf as UnitType]?.label ?? conf);
      return enemy
        ? `Enemy observer reported a ${label}${where}`
        : `New contact — ${label}${where} (±${Math.round(num(ev.payload.errorM))} m)`;
    }
    case 'nai.contact':
      return `Contact in ${str(ev.payload.name, 'a named area of interest')}${where}`;
    case 'pl.crossed':
      return `${cs} crossed ${str(ev.payload.name, 'a phase line')}`;
    case 'unit.broken':
      return `${enemy ? 'Enemy ' : ''}${cs} broke and is withdrawing`;
    case 'unit.destroyed':
      return `${enemy ? 'Enemy ' : ''}${cs} destroyed`;
    case 'unit.pinned':
      return `${enemy ? 'Enemy ' : ''}${cs} pinned by fire`;
    case 'unit.fuelLow':
      return `${cs} fuel ${Math.round(num(ev.payload.fuel))}%`;
    case 'force.fuelLow':
      return `Task force fuel ${Math.round(num(ev.payload.avgFuel))}% — approaching culmination`;
    case 'culminated':
      return `Task force culminated at ${Math.round(num(ev.payload.avgStrength))}% average strength`;
    case 'obj.seized':
      return `${str(ev.payload.name, 'Objective')} seized by ${num(ev.payload.platoons)} platoons`;
    case 'net.saturated':
      return `Command net saturated at ${Math.round(num(ev.payload.occupancy) * 100)}% — orders queueing`;
    case 'uas.lost':
      return `${cs} airframe lost`;
    case 'phase':
      return `Phase: ${str(ev.payload.phase)}`;
    case 'sitrep':
      return str(ev.payload.text, 'SITREP');
    default:
      return `${ev.kind}${where}`;
  }
}

/**
 * The tick each completed order finished at. Orders carry issue/transmit/execute ticks but not a done
 * tick, so it is recovered from the log: `order.done` events for a unit are matched, in log order, to
 * that unit's own orders in issue order. Two identical cards to one unit in flight at once would be the
 * ambiguous case; the sim never has one, because a re-task supersedes the order it interrupts.
 */
export function doneTicks(orders: Order[], events: Event[]): Map<number, number> {
  const doneByUnit = new Map<number, number[]>();
  for (const ev of events) {
    if (ev.kind !== 'order.done' || ev.unitId === undefined) continue;
    const list = doneByUnit.get(ev.unitId);
    if (list) list.push(ev.t); else doneByUnit.set(ev.unitId, [ev.t]);
  }
  const cursor = new Map<number, number>();
  const out = new Map<number, number>();
  for (const o of [...orders].sort((a, b) => a.issuedAt - b.issuedAt || a.id - b.id)) {
    if (o.status !== 'done') continue;
    const list = doneByUnit.get(o.unitId);
    if (!list) continue;
    const i = cursor.get(o.unitId) ?? 0;
    if (i >= list.length) continue;
    cursor.set(o.unitId, i + 1);
    out.set(o.id, list[i]);
  }
  return out;
}

/** Losses per side from the last ground-truth frame, joined to the unit list for sides. */
export function lossesFromTruth(
  truth: { tick: number; units: { id: number; alive: boolean }[] }[],
  units: Unit[],
): { blue: number; red: number; blueTotal: number; redTotal: number } {
  const last = truth[truth.length - 1];
  const sideOf = new Map(units.map((u) => [u.id, u.side]));
  let blue = 0, red = 0;
  for (const u of last?.units ?? []) {
    if (u.alive) continue;
    if (sideOf.get(u.id) === 1) red++; else blue++;
  }
  return {
    blue, red,
    blueTotal: units.filter((u) => u.side === 0).length,
    redTotal: units.filter((u) => u.side === 1).length,
  };
}

/**
 * Units that went combat-ineffective per side, from the log.
 *
 * A broken platoon is still on the map and still in the unit count, so it never appeared in the losses
 * line: the debrief read "0 of 24 lost" on a run with five broken platoons in it. Broken and destroyed
 * are different costs and are now counted, and stated, separately.
 */
export function brokenFromEvents(events: Event[], units: Unit[]): { blue: number; red: number } {
  const sideOf = new Map(units.map((u) => [u.id, u.side]));
  const blue = new Set<number>();
  const red = new Set<number>();
  for (const ev of events) {
    if (ev.kind !== 'unit.broken' || ev.unitId === undefined) continue;
    (sideOf.get(ev.unitId) === 1 ? red : blue).add(ev.unitId);
  }
  return { blue: blue.size, red: red.size };
}

/** The numbers the stats panel shows and the narrative prompt is grounded in. */
export interface RunStats {
  /** Destroyed, from the ground-truth frames. */
  blueLosses: number;
  /** Combat-ineffective but still on the map, from the log. Counted apart from destroyed. */
  blueBroken: number;
  blueTotal: number;
  redLosses: number;
  redBroken: number;
  redTotal: number;
  fuelPct: number;
  ammoPct: number;
  strengthPct: number;
  contacts: number;
  contactsCorrectType: number;
  /** `null` when no contact was ever reported: there was no picture to be accurate about. */
  meanPositionErrorM: number | null;
  /** Fraction typed correctly, or `null` when there were no contacts. */
  typeRate: number | null;
  /** Mean uncertainty the observers themselves quoted with their reports (the ±m on a SPOTREP). */
  meanReportedErrorM: number;
  enemiesNeverSeen: number;
  durationMin: number;
}

/**
 * A flat `Record<string, number>` of the same figures, for `DebriefContext.stats`.
 *
 * A figure that is `null` - the picture stats on a run with no contacts - is left out entirely rather
 * than sent as 0: the rapporteur is told only what is known, so it cannot write "mean position error
 * 0 m" about a fight in which nobody saw anything.
 */
export function statsRecord(s: RunStats): Record<string, number> {
  return {
    blueDestroyed: s.blueLosses,
    blueBroken: s.blueBroken,
    redDestroyed: s.redLosses,
    redBroken: s.redBroken,
    avgFuelPct: Math.round(s.fuelPct),
    avgAmmoPct: Math.round(s.ammoPct),
    avgStrengthPct: Math.round(s.strengthPct),
    contactsReported: s.contacts,
    ...(s.contacts > 0 ? { contactsCorrectType: s.contactsCorrectType } : {}),
    ...(s.meanPositionErrorM !== null ? { meanPositionErrorM: Math.round(s.meanPositionErrorM) } : {}),
    ...(s.contacts > 0 ? { meanReportedUncertaintyM: Math.round(s.meanReportedErrorM) } : {}),
    enemiesNeverSeen: s.enemiesNeverSeen,
    durationMin: Math.round(s.durationMin),
  };
}

// ---- replay ----------------------------------------------------------------------------------------

/** One reported enemy contact and the window it stood in blue's picture. */
export interface ContactSpan {
  enemyId: number;
  eventId: number;
  cell: number;
  from: number;
  /** Tick the contact went stale (`contact.lost`), or Infinity if it never did. */
  until: number;
}

/**
 * The contact.new / contact.lost pairs on blue's side of the log. A contact can be reacquired, so each
 * `contact.new` opens its own span and the next `contact.lost` for that enemy closes the open one.
 */
export function contactSpans(events: Event[]): ContactSpan[] {
  const spans: ContactSpan[] = [];
  const open = new Map<number, ContactSpan>();
  for (const ev of [...events].sort((a, b) => a.t - b.t || a.id - b.id)) {
    if (ev.side !== 0) continue;
    const enemyId = typeof ev.payload.enemyId === 'number' ? ev.payload.enemyId : undefined;
    if (enemyId === undefined) continue;
    if (ev.kind === 'contact.new' && ev.cell !== undefined) {
      const span: ContactSpan = { enemyId, eventId: ev.id, cell: ev.cell, from: ev.t, until: Infinity };
      spans.push(span);
      open.set(enemyId, span);
    } else if (ev.kind === 'contact.lost') {
      const span = open.get(enemyId);
      if (span) { span.until = ev.t; open.delete(enemyId); }
    }
  }
  return spans;
}

/**
 * The observed type and reported position error behind each `contact.new` event, keyed by that event's
 * own id (which `ContactSpan.eventId` points back to). Kept separate from `contactSpans` rather than
 * added to its return shape, so the span windows the decision log and the replay both already rely on
 * stay exactly as tested. The replay uses this, with the contact's age at the scrubbed tick, to size the
 * uncertainty ring the same way `src/cop/spread.ts` sizes it live (spec §7/§8).
 */
export function contactReportMeta(events: Event[]): Map<number, { typeConf: Contact['typeConf']; errorM: number }> {
  const out = new Map<number, { typeConf: Contact['typeConf']; errorM: number }>();
  for (const ev of events) {
    if (ev.kind !== 'contact.new' || ev.side !== 0) continue;
    const typeConf = typeof ev.payload.typeConf === 'string' ? (ev.payload.typeConf as Contact['typeConf']) : 'unknown';
    const errorM = typeof ev.payload.errorM === 'number' ? ev.payload.errorM : 0;
    out.set(ev.id, { typeConf, errorM });
  }
  return out;
}

/** The last ground-truth frame recorded at or before `tick`. */
export function frameAt(truth: TruthFrame[], tick: number): TruthFrame | null {
  let best: TruthFrame | null = null;
  for (const f of truth) {
    if (f.tick > tick) break;
    best = f;
  }
  return best ?? truth[0] ?? null;
}
